import { AgreementTypeEnum, FavoriteType } from "__generated__/enums";
import { AgreementType } from "view/agreementtypes/types/AgreementType";
import { intl } from "LocaleWrapper";
import { Popper } from "@mui/material";
import FavoriteSelectOption from "components/selects/FavoriteSelectOption";
import SelectPopover from "@cofano/cck/Select/SelectPopover";
import { StyledPopper } from "@cofano/cck/Select/Styled";
import { Instance } from "@popperjs/core";
import { useRef, useCallback, useMemo } from "react";

type AgreementOption = {
	type: AgreementTypeEnum;
	id: number;
	label: string;
};

type AgreementSelectPopoverProps = {
	open: boolean;
	anchorEl: HTMLElement | null;
	options: AgreementTypeEnum[];
	agreementTypes?: AgreementType[];
	onSelect: (type: AgreementTypeEnum) => void;
	onClose: () => void;
};

const AgreementSelectPopover = ({
	open,
	anchorEl,
	options,
	agreementTypes,
	onSelect,
	onClose,
}: AgreementSelectPopoverProps) => {
	const popperRef = useRef<Instance>(null);

	const transformedOptions: AgreementOption[] = useMemo(() =>
		options.map((type) => ({
			type,
			id: agreementTypes?.find(x => x.type === type)?.id || 0,
			label: intl.formatMessage({ id: `AGREEMENT_TYPE_${type}` }),
		})),
	[ options, agreementTypes ],
	);

	const getOptionValue = useCallback((option: AgreementOption) => option.type, []);
	const getOptionLabel = useCallback((option: AgreementOption) => option.label, []);

	const renderOption = useCallback((
		props: React.LiHTMLAttributes<HTMLLIElement>,
		option: AgreementOption,
	) => (
		<li {...props}
			key={option.type}
			onMouseDown={(e) => {
				// required for not closing the select immediately
				// before completing the selection,
				// now that it extends past the dialog's height
				e.stopPropagation();
				e.preventDefault();
			}}>
			<FavoriteSelectOption
				entityId={option.id}
				entityType={FavoriteType.AgreementType}>
				{option.label}
			</FavoriteSelectOption>
		</li>
	), []);

	const handleChange = useCallback((value: AgreementOption | null) => {
		if (value) {
			onSelect(value.type);
			onClose();
		}
	}, [ onSelect, onClose ]);

	return (
		<Popper
			open={open}
			anchorEl={anchorEl}
			placement="bottom-start"
			style={{ zIndex: 1500 }}
			popperRef={popperRef}>
			<StyledPopper>
				<SelectPopover<AgreementOption, false>
					fetchOptions={transformedOptions}
					onChange={handleChange}
					handleClose={onClose}
					selectedOption={null}
					popperRef={popperRef}
					getOptionValue={getOptionValue}
					getOptionLabel={getOptionLabel}
					renderOption={renderOption}
					excluded={false}
					amountLabel=""
					createOptionHeader=""
					createOptionLabel=""
					noOptionsLabel={intl.formatMessage({ id: "NO_OPTIONS" })}
					typeToSearchLabel={intl.formatMessage({ id: "TYPE_TO_SEARCH" })}
					loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
				/>
			</StyledPopper>
		</Popper>
	);
};

export default AgreementSelectPopover;

